// /admin/marketing/volante
// Volante A4 imprimible para repartir en porterías de barrio.
// Diseño limpio en B&N (ahorra tinta) + acentos teal opcionales.
// QR redirige a https://limpack.uy/suscribirme

import { useEffect, useRef, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
function loadQRCodeLib() {
  return new Promise((resolve, reject) => {
    if (window.QRCode) return resolve(window.QRCode)
    const s = document.createElement('script')
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js'
    s.onload = () => resolve(window.QRCode)
    s.onerror = reject
    document.body.appendChild(s)
  })
}

// UTM tracking: cada QR/canal tiene su utm_source para medir de dónde vino la suscripción
const URL_SUSCRIBIRME = 'https://limpack.uy/suscribirme?utm_source=volante'
const URL_PUBLICA = 'limpack.uy/suscribirme'

export default function MarketingVolante() {
  const qrRef = useRef(null)
  const [color, setColor] = useState('color')  // color | bn
  const [qrReady, setQrReady] = useState(false)

  useEffect(() => {
    let cancel = false
    let attempts = 0

    async function renderQR() {
      attempts++
      try {
        await loadQRCodeLib()
        if (cancel || !qrRef.current) {
          if (attempts < 5) setTimeout(renderQR, 500)
          return
        }
        qrRef.current.innerHTML = ''
        new window.QRCode(qrRef.current, {
          text: URL_SUSCRIBIRME,
          width: 220,
          height: 220,
          colorDark: '#0f1f3d',
          colorLight: '#ffffff',
          correctLevel: window.QRCode.CorrectLevel.M,
        })
        setQrReady(true)
      } catch (e) {
        console.error('QR error:', e)
        if (attempts < 5) setTimeout(renderQR, 1000)
      }
    }

    renderQR()
    return () => { cancel = true }
  }, [])

  function imprimir() {
    window.print()
  }

  return (
    <AdminLayout>
      <div className="page no-print">
        <h1>📄 Volante A4 — Vecinos</h1>
        <p className="muted">
          Para imprimir y dejar en porterías. QR apunta a:<br/>
          <code style={{ background: '#f1f5f9', padding: '4px 8px', borderRadius: 4, fontSize: 12, marginTop: 4, display: 'inline-block' }}>{URL_SUSCRIBIRME}</code><br/>
          <small style={{ color: '#64748b' }}>El parámetro <code>utm_source=volante</code> identifica que el cliente vino del volante físico.</small>
        </p>

        <div className="vol-controles">
          <button className="vol-btn-print" onClick={imprimir}>
            🖨️ Imprimir / Descargar PDF
          </button>
          <label className="vol-toggle">
            <input type="radio" name="color" value="color" checked={color === 'color'} onChange={e => setColor(e.target.value)} />
            <span>🎨 Color</span>
          </label>
          <label className="vol-toggle">
            <input type="radio" name="color" value="bn" checked={color === 'bn'} onChange={e => setColor(e.target.value)} />
            <span>🖤 B&N (ahorra tinta)</span>
          </label>
        </div>

        <div className="vol-tip">
          💡 Para el mejor resultado: imprimir en hoja A4 blanca, calidad <strong>"Normal"</strong>, sin márgenes ajustados.
          Configurar la impresora a "Tamaño real" (no escalar al 100%).
        </div>
      </div>

      {/* ════════ VOLANTE A4 ════════ */}
      <div className={`volante volante-${color}`}>
        {/* Header con logo VERTICAL (manual de marca v2.0: "pieza principal · cover · packaging · hero") */}
        <div className="vol-header">
          <img src="/logo-vertical.png" alt="Limpack — Limpieza, resuelta cada mes." className="vol-logo-img" />
        </div>

        {/* Hero */}
        <div className="vol-hero">
          <h1>Tu kit de limpieza, <span>en la puerta de tu casa.</span> Cada mes.</h1>
        </div>

        {/* Banner Ahorro */}
        <div className="vol-ahorro">
          <div className="vol-ahorro-icon">💵</div>
          <div className="vol-ahorro-text">
            <strong>Ahorrás todos los meses.</strong>
            <span>Las mismas marcas que comprás en el super, <strong>al menos un 10% más baratas</strong>.</span>
          </div>
        </div>

        {/* Qué incluye + grilla productos */}
        <div className="vol-incluye">
          <div className="vol-incluye-titulo">¿Qué viene en cada kit?</div>
          <div className="vol-incluye-bajada">
            <strong>Cantidades calculadas según los que viven en tu casa.</strong> Sin sobrante, sin que falte.
          </div>
          <div className="vol-incluye-grid">
            <span>🧴 Lavarropas líquido</span>
            <span>🧺 Suavizante</span>
            <span>🧽 Lavavajillas</span>
            <span>🚽 Lavandina</span>
            <span>✨ Limpiador multiuso</span>
            <span>🧼 Jabón de manos</span>
            <span>🧻 Papel higiénico</span>
            <span>📜 Papel cocina</span>
            <span>🛍️ Bolsas residuos</span>
            <span>🧽 Esponjas</span>
          </div>
        </div>

        {/* Tipos de kit Essential */}
        <div className="vol-section-titulo">
          Elegí tu kit mensual <small className="vol-iva-tag">precios con IVA incluido</small>
        </div>
        <div className="vol-kits">
          <div className="vol-kit vol-kit-essential">
            <div className="vol-kit-nombre">Solo</div>
            <div className="vol-kit-personas">1–2 personas</div>
            <div className="vol-kit-precio">$1.150<small>/mes</small></div>
          </div>
          <div className="vol-kit vol-kit-essential">
            <div className="vol-kit-nombre">Familiar</div>
            <div className="vol-kit-personas">3–4 personas</div>
            <div className="vol-kit-precio">$1.890<small>/mes</small></div>
          </div>
          <div className="vol-kit vol-kit-essential">
            <div className="vol-kit-nombre">Hogar+</div>
            <div className="vol-kit-personas">5+ personas</div>
            <div className="vol-kit-precio">$2.550<small>/mes</small></div>
          </div>
        </div>
        <div className="vol-tier-nota">
          También tenemos <strong>línea Premium con productos concentrados</strong> — entrá a limpack.uy y mirá todo.
        </div>

        {/* QR + CTA */}
        <div className="vol-qr-section">
          <div className="vol-qr-wrap">
            <div ref={qrRef} className="vol-qr"></div>
          </div>
          <div className="vol-qr-text">
            <div className="vol-qr-titulo">Escaneá<br/>y suscribite</div>
            <div className="vol-qr-sub">Elegís tu kit en 2 minutos · Pausás o cancelás cuando quieras</div>
            <div className="vol-qr-link">{URL_PUBLICA}</div>
          </div>
        </div>

        {/* Footer compacto con marcas + contacto */}
        <div className="vol-footer">
          <div className="vol-footer-marcas">
            Trabajamos con: <strong>Cottonmax · Brilha Sul · Suprema · CIF · Clarys · Bello · Yallis</strong>
          </div>
          <div className="vol-footer-contacto">
            <strong>limpack.uy</strong> · @limpackuy · ☎ <strong>098 610 001</strong>
          </div>
        </div>
      </div>

      <style>{`
        /* ═══ Solo se ve en pantalla, no en print ═══ */
        .no-print { padding-bottom: 20px; }

        .vol-controles {
          display: flex; gap: 14px; align-items: center; flex-wrap: wrap;
          margin: 14px 0 18px;
        }
        .vol-btn-print {
          padding: 12px 22px;
          background: #1a3461; color: #fff;
          border: 0; border-radius: 999px;
          font-weight: 800; font-size: 15px; cursor: pointer;
          box-shadow: 0 4px 14px rgba(26,52,97,.25);
        }
        .vol-toggle {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 13px; color: #475569; cursor: pointer;
          padding: 8px 12px; background: #fff;
          border: 1px solid #e5e7eb; border-radius: 8px;
        }
        .vol-tip {
          background: #fef3c7; border: 1px solid #fcd34d; color: #78350f;
          padding: 12px 14px; border-radius: 10px; font-size: 13px; line-height: 1.5;
          margin-bottom: 16px;
        }

        /* ═══ El volante ═══ */
        .volante {
          width: 210mm;
          height: 297mm;
          margin: 20px auto;
          background: #fff;
          padding: 10mm 12mm;
          box-sizing: border-box;
          box-shadow: 0 8px 30px rgba(0,0,0,.15);
          font-family: 'Inter', system-ui, sans-serif;
          color: #0f1f3d;
          display: flex; flex-direction: column;
          gap: 3mm;
          overflow: hidden;
        }

        /* Header con logo PNG */
        .vol-header {
          text-align: center;
          padding-bottom: 3mm;
          border-bottom: 1.5px solid #1a3461;
        }
        .vol-logo-img {
          max-width: 50mm;
          max-height: 30mm;
          width: auto;
          height: auto;
          display: inline-block;
          object-fit: contain;
          padding: 2mm 0;
        }
        .volante-bn .vol-logo-img { filter: grayscale(100%); }

        /* Hero compacto */
        .vol-hero h1 {
          font-size: 20pt;
          line-height: 1.1;
          font-weight: 900;
          color: #1a3461;
          letter-spacing: -0.02em;
          margin: 0;
          text-align: center;
        }
        .vol-hero h1 span {
          color: #1a3461;
          font-style: italic;
          font-weight: 800;
        }
        .volante-color .vol-hero h1 span { color: #00A8B5; }

        /* 3 pasos */
        .vol-pasos {
          display: flex;
          align-items: center;
          gap: 2mm;
          padding: 3mm 4mm;
          background: #f8fafc;
          border-radius: 3mm;
        }
        .volante-color .vol-pasos { background: #f0fdfa; }
        .vol-paso {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 3mm;
        }
        .vol-paso-num {
          width: 10mm; height: 10mm;
          border-radius: 50%;
          background: #1a3461;
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-weight: 900;
          font-size: 14pt;
          flex-shrink: 0;
        }
        .volante-color .vol-paso-num { background: #00A8B5; }
        .vol-paso-text {
          font-size: 9pt;
          line-height: 1.3;
          color: #475569;
        }
        .vol-paso-text strong { color: #1a3461; }
        .vol-paso-arrow {
          color: #94a3b8;
          font-size: 14pt;
          font-weight: 900;
        }

        /* Beneficios — 3 inline compactos */
        .vol-beneficios {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 3mm;
        }
        .vol-benef {
          display: flex;
          align-items: center;
          gap: 2mm;
          padding: 3mm 4mm;
          border: 1.5px solid #1a3461;
          border-radius: 3mm;
          font-size: 9pt;
          color: #475569;
        }
        .volante-color .vol-benef { border-color: #00A8B5; }
        .vol-benef strong { color: #1a3461; }
        .vol-benef-icon { font-size: 16pt; flex-shrink: 0; }

        /* Banner Ahorro */
        .vol-ahorro {
          display: flex;
          align-items: center;
          gap: 3mm;
          padding: 3mm 4mm;
          background: linear-gradient(90deg, #00A8B5 0%, #00d4e0 100%);
          color: #fff;
          border-radius: 3mm;
        }
        .volante-bn .vol-ahorro {
          background: #1a3461 !important;
        }
        .vol-ahorro-icon { font-size: 26pt; line-height: 1; }
        .vol-ahorro-text { display: flex; flex-direction: column; gap: 1mm; }
        .vol-ahorro-text strong:first-child {
          font-size: 13pt;
          font-weight: 900;
          letter-spacing: -0.01em;
        }
        .vol-ahorro-text span {
          font-size: 9pt;
          line-height: 1.4;
          opacity: 0.95;
        }
        .vol-ahorro-text span strong { color: #fff; font-weight: 800; }

        /* Sección titulo */
        .vol-section-titulo {
          font-size: 13pt;
          font-weight: 800;
          color: #1a3461;
          text-align: center;
          margin-top: 2mm;
        }
        .vol-iva-tag {
          font-size: 8pt;
          font-weight: 600;
          color: #64748b;
          font-style: italic;
          margin-left: 4mm;
        }

        /* Tier rows */
        .vol-tier-row {
          display: flex;
          gap: 3mm;
          align-items: stretch;
          margin-bottom: 3mm;
        }
        .vol-tier-label {
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          padding: 3mm 2mm;
          font-size: 9pt;
          font-weight: 900;
          letter-spacing: 0.15em;
          color: #fff;
          border-radius: 2mm;
          text-align: center;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .vol-tier-essential { background: #1a3461; }
        .vol-tier-premium { background: #00A8B5; }
        .volante-bn .vol-tier-essential,
        .volante-bn .vol-tier-premium { background: #000 !important; }

        .vol-tier-nota {
          font-size: 8.5pt;
          color: #475569;
          text-align: center;
          padding: 2mm;
          line-height: 1.5;
        }
        .vol-tier-nota strong { color: #1a3461; }

        /* Tipos de kit */
        .vol-kits {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 3mm;
          flex: 1;
        }
        .vol-kit {
          background: #f8fafc;
          border: 2px solid #1a3461;
          border-radius: 3mm;
          padding: 3mm 2mm;
          text-align: center;
        }
        .volante-color .vol-kit-essential {
          background: linear-gradient(180deg, #fff 0%, #f1f5f9 100%);
          border-color: #1a3461;
        }
        .volante-color .vol-kit-premium {
          background: linear-gradient(180deg, #fff 0%, #ecfeff 100%);
          border-color: #00A8B5;
        }
        .vol-kit-nombre {
          font-size: 14pt;
          font-weight: 900;
          color: #1a3461;
          letter-spacing: -0.01em;
        }
        .vol-kit-personas {
          font-size: 9pt;
          color: #64748b;
          margin: 1mm 0;
        }
        .vol-kit-precio {
          font-size: 18pt;
          font-weight: 900;
          color: #1a3461;
          letter-spacing: -0.02em;
          margin-top: 1mm;
        }
        .volante-color .vol-kit-essential .vol-kit-precio { color: #1a3461; }
        .volante-color .vol-kit-premium .vol-kit-precio { color: #00A8B5; }
        .vol-kit-precio small {
          font-size: 9pt;
          font-weight: 600;
          color: #64748b;
        }
        .vol-kit-ahorro {
          font-size: 7.5pt;
          color: #00A8B5;
          margin-top: 2mm;
          font-weight: 700;
          line-height: 1.2;
        }
        .volante-bn .vol-kit-ahorro { color: #000; }

        /* Qué incluye */
        .vol-incluye {
          background: #f1f5f9;
          border-radius: 3mm;
          padding: 3mm 4mm;
        }
        .volante-color .vol-incluye { background: #f0fdfa; }
        .vol-incluye-titulo {
          font-size: 11pt;
          font-weight: 800;
          color: #1a3461;
          margin-bottom: 2mm;
        }
        .vol-incluye-bajada {
          font-size: 9pt;
          color: #475569;
          line-height: 1.45;
          margin-bottom: 3mm;
          padding-bottom: 3mm;
          border-bottom: 1px dashed #cbd5e1;
        }
        .vol-incluye-bajada strong {
          color: #1a3461;
          font-weight: 800;
        }
        .vol-incluye-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2mm 4mm;
          font-size: 9pt;
          color: #475569;
        }

        /* La Tienda */
        .vol-tienda {
          display: flex;
          align-items: center;
          gap: 3mm;
          padding: 2.5mm 4mm;
          background: #1a3461;
          color: #fff;
          border-radius: 3mm;
        }
        .volante-color .vol-tienda { background: #1a3461; }
        .vol-tienda-icon { font-size: 22pt; }
        .vol-tienda-text { font-size: 10pt; line-height: 1.4; }
        .vol-tienda-text strong { color: #fff; }

        /* QR + CTA */
        .vol-qr-section {
          display: flex;
          gap: 5mm;
          align-items: center;
          padding: 4mm;
          background: #f8fafc;
          border-radius: 3mm;
          border: 2px dashed #1a3461;
        }
        .volante-color .vol-qr-section { border-color: #00A8B5; }
        .vol-qr-wrap {
          background: #fff;
          padding: 3mm;
          border-radius: 2mm;
          flex-shrink: 0;
        }
        .vol-qr-text { flex: 1; }
        .vol-qr-titulo {
          font-size: 18pt;
          font-weight: 900;
          color: #1a3461;
          line-height: 1.05;
          letter-spacing: -0.02em;
        }
        .vol-qr-sub {
          font-size: 10pt;
          color: #475569;
          margin-top: 2mm;
        }
        .vol-qr-link {
          margin-top: 3mm;
          font-size: 10pt;
          color: #1a3461;
          font-weight: 700;
        }

        /* Footer compacto: marcas + contacto */
        .vol-footer {
          margin-top: auto;
          padding-top: 3mm;
          border-top: 1px solid #cbd5e1;
          font-size: 8.5pt;
          color: #475569;
          text-align: center;
          line-height: 1.5;
        }
        .vol-footer strong { color: #1a3461; }
        .vol-footer-marcas { margin-bottom: 1.5mm; }
        .vol-footer-contacto { font-size: 9pt; }

        /* ═══ MODO B&N ═══ */
        .volante-bn .vol-logo strong,
        .volante-bn .vol-hero h1 span,
        .volante-bn .vol-benef,
        .volante-bn .vol-qr-section {
          color: #000 !important;
          background: #fff !important;
          border-color: #000 !important;
        }
        .volante-bn .vol-tag,
        .volante-bn .vol-benef-text,
        .volante-bn .vol-footer { color: #333 !important; }
        .volante-bn .vol-logo,
        .volante-bn .vol-hero h1,
        .volante-bn .vol-benef-titulo,
        .volante-bn .vol-qr-titulo,
        .volante-bn .vol-qr-link,
        .volante-bn .vol-footer strong { color: #000 !important; }
        .volante-bn .vol-header { border-bottom-color: #000 !important; }

        /* ═══ PRINT ═══ */
        @media print {
          body, html { background: #fff !important; margin: 0; padding: 0; }
          .no-print, nav, header, .admin-nav, .admin-nav-grouped { display: none !important; }
          .volante { box-shadow: none !important; margin: 0 !important; padding: 12mm !important; page-break-inside: avoid; }
        }
        @page { size: A4; margin: 0; }
      `}</style>
    </AdminLayout>
  )
}
